/**
 * Protocol Handler - Protocol-agnostic communication layer
 *
 * STRICT RULES - THIS FILE MUST ONLY CONTAIN:
 * ✅ Protocol abstraction and delegation
 * ✅ Environment-based protocol selection
 * ✅ Unified interface for all protocols
 * ✅ Callback pattern delegation
 *
 * FORBIDDEN:
 * ❌ React imports or UI libraries
 * ❌ Direct atom mutations (must use callbacks)
 * ❌ Component imports
 * ❌ UI state management
 * ❌ JSX or UI rendering code
 */

import type {
  messageStructureTypes,
  userAccountsTypes,
  ORPCClientContext,
} from "@pineseed/spec";

// Re-export types for convenience
type SendMessageRequest = messageStructureTypes["SendMessageRequest"];
type ChatMessage = messageStructureTypes["ChatMessage"];
type WorkspacesList = userAccountsTypes["WorkspacesList"];

// Protocol-agnostic callback interface (matches existing ORPCHandlerCallbacks)
export interface ProtocolHandlerCallbacks {
  onMessage?: (message: ChatMessage) => void;
  onConnectionStatusChange?: (connected: boolean) => void;
  onError?: (error: Error) => void;
}

// Protocol-agnostic configuration interface
export interface ProtocolConfig {
  baseUrl: string;
  token?: string;
  callbacks?: ProtocolHandlerCallbacks;
}

// Protocol adapter interface - minimal and focused
interface ProtocolAdapter {
  initialize(config: ProtocolConfig): Promise<void>;
  sendMessage(
    request: SendMessageRequest,
    context?: ORPCClientContext,
  ): Promise<ChatMessage>;
  getUserWorkspaces(context?: ORPCClientContext): Promise<WorkspacesList>;
  subscribeToMessages(
    params: {
      workspaceId: string;
      conversationId?: string;
      threadId?: string;
    },
    context?: ORPCClientContext,
  ): Promise<AsyncIterable<ChatMessage | { type: "heartbeat" }>>;
  createThread(
    request: messageStructureTypes["CreateThreadRequest"],
  ): Promise<messageStructureTypes["ChatThread"]>;
  createConversation(
    request: messageStructureTypes["CreateConversationRequest"],
  ): Promise<messageStructureTypes["ChatConversation"]>;
  getConversations(params: {
    workspaceId: string;
    limit?: number;
  }): Promise<messageStructureTypes["ConversationListResponse"]>;
  getChatCache(params: {
    workspaceId: string;
  }): Promise<messageStructureTypes["ChatCacheResponse"]>;
  getMessages(params: {
    workspaceId: string;
    conversationId: string;
    threadId: string;
    limit?: number;
    before?: string;
  }): Promise<messageStructureTypes["MessageListResponse"]>;
  setToken(token: string): void;
  setCallbacks(callbacks: ProtocolHandlerCallbacks): void;
  cleanup(): Promise<void>;
}

/**
 * oRPC Protocol Adapter - Wraps existing ORPCHandler
 * Zero behavior change - pure delegation
 */
class ORPCAdapter implements ProtocolAdapter {
  private orpcHandler: typeof import("./orpc-handler").orpcHandler | null =
    null;

  async initialize(config: ProtocolConfig): Promise<void> {
    // Dynamic import to maintain architectural boundaries
    const { orpcHandler } = await import("./orpc-handler");
    this.orpcHandler = orpcHandler;

    // Direct delegation - zero behavior change
    return this.orpcHandler.getInstance().initialize(config);
  }

  async sendMessage(
    request: SendMessageRequest,
    context?: ORPCClientContext,
  ): Promise<ChatMessage> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().sendMessage(request, context);
  }

  async getUserWorkspaces(
    context?: ORPCClientContext,
  ): Promise<WorkspacesList> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().getUserWorkspaces(context);
  }

  async subscribeToMessages(
    params: {
      workspaceId: string;
      conversationId?: string;
      threadId?: string;
    },
    context?: ORPCClientContext,
  ): Promise<AsyncIterable<ChatMessage | { type: "heartbeat" }>> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().subscribeToMessages(params, context);
  }

  async createThread(
    request: messageStructureTypes["CreateThreadRequest"],
  ): Promise<messageStructureTypes["ChatThread"]> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().createThread(request);
  }

  async createConversation(
    request: messageStructureTypes["CreateConversationRequest"],
  ): Promise<messageStructureTypes["ChatConversation"]> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().createConversation(request);
  }

  async getConversations(params: {
    workspaceId: string;
    limit?: number;
  }): Promise<messageStructureTypes["ConversationListResponse"]> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().getConversations(params);
  }

  async getChatCache(params: {
    workspaceId: string;
  }): Promise<messageStructureTypes["ChatCacheResponse"]> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().getChatCache(params);
  }

  async getMessages(params: {
    workspaceId: string;
    conversationId: string;
    threadId: string;
    limit?: number;
    before?: string;
  }): Promise<messageStructureTypes["MessageListResponse"]> {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    return this.orpcHandler.getInstance().getMessages(params);
  }

  setToken(token: string): void {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    this.orpcHandler.getInstance().setToken(token);
  }

  setCallbacks(callbacks: ProtocolHandlerCallbacks): void {
    if (!this.orpcHandler) throw new Error("oRPC handler not initialized");
    this.orpcHandler.getInstance().setCallbacks(callbacks);
  }

  async cleanup(): Promise<void> {
    // oRPC handler doesn't have cleanup method, so no-op
    return Promise.resolve();
  }
}

/**
 * REST Protocol Adapter - Future implementation
 */
class RESTAdapter implements ProtocolAdapter {
  private baseUrl: string = "";
  private token: string | null = null;
  private callbacks: ProtocolHandlerCallbacks = {};

  async initialize(config: ProtocolConfig): Promise<void> {
    this.baseUrl = config.baseUrl;
    this.token = config.token || null;
    this.callbacks = config.callbacks || {};

    console.log("🌐 REST adapter initialized with baseUrl:", this.baseUrl);
  }

  async sendMessage(request: SendMessageRequest): Promise<ChatMessage> {
    // Validate request using existing schema
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    const validatedRequest =
      messageStructureSchemas.SendMessageRequest.parse(request);

    console.log("🔄 Sending message to REST API:", validatedRequest);

    const response = await fetch(`${this.baseUrl}/api/chat/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: JSON.stringify(validatedRequest),
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Validate response using existing schema
    const validatedResponse = messageStructureSchemas.ChatMessage.parse(data);

    // Emit callback
    this.callbacks.onMessage?.(validatedResponse);

    return validatedResponse;
  }

  async getUserWorkspaces(): Promise<WorkspacesList> {
    const response = await fetch(`${this.baseUrl}/user/workspaces`, {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Validate response using existing schema
    const { userAccountsSchemas } = await import("@pineseed/spec");
    return userAccountsSchemas.WorkspacesList.parse(data);
  }

  async createThread(
    request: messageStructureTypes["CreateThreadRequest"],
  ): Promise<messageStructureTypes["ChatThread"]> {
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    const validatedRequest =
      messageStructureSchemas.CreateThreadRequest.parse(request);

    const response = await fetch(`${this.baseUrl}/chat/threads`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: JSON.stringify(validatedRequest),
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return messageStructureSchemas.ChatThread.parse(data);
  }

  async createConversation(
    request: messageStructureTypes["CreateConversationRequest"],
  ): Promise<messageStructureTypes["ChatConversation"]> {
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    const validatedRequest =
      messageStructureSchemas.CreateConversationRequest.parse(request);

    const response = await fetch(`${this.baseUrl}/chat/conversations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: JSON.stringify(validatedRequest),
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return messageStructureSchemas.ChatConversation.parse(data);
  }

  async getConversations(params: {
    workspaceId: string;
    limit?: number;
  }): Promise<messageStructureTypes["ConversationListResponse"]> {
    const url = new URL(`${this.baseUrl}/chat/conversations`);
    url.searchParams.set("workspaceId", params.workspaceId);
    if (params.limit) url.searchParams.set("limit", params.limit.toString());

    const response = await fetch(url.toString(), {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    return messageStructureSchemas.ConversationListResponse.parse(data);
  }

  async getChatCache(params: {
    workspaceId: string;
  }): Promise<messageStructureTypes["ChatCacheResponse"]> {
    const url = new URL(`${this.baseUrl}/chat/cache`);
    url.searchParams.set("workspaceId", params.workspaceId);

    const response = await fetch(url.toString(), {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    return messageStructureSchemas.ChatCacheResponse.parse(data);
  }

  async getMessages(params: {
    workspaceId: string;
    conversationId: string;
    threadId: string;
    limit?: number;
    before?: string;
  }): Promise<messageStructureTypes["MessageListResponse"]> {
    const url = new URL(`${this.baseUrl}/chat/messages`);
    url.searchParams.set("workspaceId", params.workspaceId);
    url.searchParams.set("conversationId", params.conversationId);
    url.searchParams.set("threadId", params.threadId);
    if (params.limit) url.searchParams.set("limit", params.limit.toString());
    if (params.before) url.searchParams.set("before", params.before);

    const response = await fetch(url.toString(), {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(
        `REST request failed: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    return messageStructureSchemas.MessageListResponse.parse(data);
  }

  async subscribeToMessages(params: {
    workspaceId: string;
    conversationId?: string;
    threadId?: string;
  }): Promise<AsyncIterable<ChatMessage | { type: "heartbeat" }>> {
    // REST implementation using Server-Sent Events
    const url = new URL(`${this.baseUrl}/chat/messages/subscribe`);
    url.searchParams.set("workspaceId", params.workspaceId);
    if (params.conversationId)
      url.searchParams.set("conversationId", params.conversationId);
    if (params.threadId) url.searchParams.set("threadId", params.threadId);

    // Pre-import schema to avoid async in event handler
    const { messageStructureSchemas } = await import(
      "@pineseed/spec"
    );
    const eventSource = new EventSource(url.toString());

    return {
      async *[Symbol.asyncIterator]() {
        const messageQueue: (ChatMessage | { type: "heartbeat" })[] = [];
        let resolve:
          | ((
              value: IteratorResult<ChatMessage | { type: "heartbeat" }>,
            ) => void)
          | null = null;
        let isComplete = false;

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "heartbeat") {
              messageQueue.push({ type: "heartbeat" });
            } else {
              // Validate message using pre-imported schema
              try {
                const message = messageStructureSchemas.ChatMessage.parse(data);
                messageQueue.push(message);
              } catch (parseError) {
                console.error("Error validating SSE message:", parseError);
                return;
              }
            }

            if (resolve) {
              const next = messageQueue.shift();
              if (next) {
                resolve({ value: next, done: false });
                resolve = null;
              }
            }
          } catch (error) {
            console.error("Error parsing SSE message:", error);
          }
        };

        eventSource.onerror = () => {
          isComplete = true;
          eventSource.close();
          if (resolve) {
            resolve({ value: undefined, done: true });
            resolve = null;
          }
        };

        while (!isComplete) {
          if (messageQueue.length > 0) {
            yield messageQueue.shift()!;
          } else {
            await new Promise<void>((res) => {
              resolve = (result) => {
                if (!result.done) {
                  res();
                }
              };
            });
          }
        }
      },
    };
  }

  setToken(token: string): void {
    this.token = token;
  }

  setCallbacks(callbacks: ProtocolHandlerCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  async cleanup(): Promise<void> {
    // Cleanup any active connections
    return Promise.resolve();
  }
}

/**
 * Protocol Handler - Environment-based protocol selection
 * Maintains exact same interface as ORPCHandler for zero-risk migration
 */
export class ProtocolHandler {
  private static instance: ProtocolHandler;
  private adapter: ProtocolAdapter | null = null;
  private protocolType: string = "orpc";

  private constructor() {}

  public static getInstance(): ProtocolHandler {
    if (!ProtocolHandler.instance) {
      ProtocolHandler.instance = new ProtocolHandler();
    }
    return ProtocolHandler.instance;
  }

  /**
   * Initialize with protocol selection based on environment
   */
  public async initialize(config: ProtocolConfig): Promise<void> {
    // Environment-based protocol selection
    this.protocolType = import.meta.env.VITE_PROTOCOL_TYPE || "orpc";

    console.log(
      `🔌 Initializing protocol handler with type: ${this.protocolType}`,
    );

    // Create appropriate adapter
    switch (this.protocolType) {
      case "rest":
        this.adapter = new RESTAdapter();
        break;
      case "orpc":
      default:
        this.adapter = new ORPCAdapter();
        break;
    }

    // Initialize the selected adapter
    await this.adapter.initialize(config);
  }

  /**
   * Send message - protocol-agnostic interface
   */
  public async sendMessage(
    request: SendMessageRequest,
    context?: ORPCClientContext,
  ): Promise<ChatMessage> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.sendMessage(request, context);
  }

  /**
   * Get user workspaces - protocol-agnostic interface
   */
  public async getUserWorkspaces(
    context?: ORPCClientContext,
  ): Promise<WorkspacesList> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.getUserWorkspaces(context);
  }

  /**
   * Subscribe to messages - protocol-agnostic interface
   */
  public async subscribeToMessages(
    params: {
      workspaceId: string;
      conversationId?: string;
      threadId?: string;
    },
    context?: ORPCClientContext,
  ): Promise<AsyncIterable<ChatMessage | { type: "heartbeat" }>> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.subscribeToMessages(params, context);
  }

  /**
   * Create thread - protocol-agnostic interface
   */
  public async createThread(
    request: messageStructureTypes["CreateThreadRequest"],
  ): Promise<messageStructureTypes["ChatThread"]> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.createThread(request);
  }

  /**
   * Create conversation - protocol-agnostic interface
   */
  public async createConversation(
    request: messageStructureTypes["CreateConversationRequest"],
  ): Promise<messageStructureTypes["ChatConversation"]> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.createConversation(request);
  }

  /**
   * Get conversations - protocol-agnostic interface
   */
  public async getConversations(params: {
    workspaceId: string;
    limit?: number;
  }): Promise<messageStructureTypes["ConversationListResponse"]> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.getConversations(params);
  }

  /**
   * Get chat cache - protocol-agnostic interface
   */
  public async getChatCache(params: {
    workspaceId: string;
  }): Promise<messageStructureTypes["ChatCacheResponse"]> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.getChatCache(params);
  }

  /**
   * Get messages - protocol-agnostic interface
   */
  public async getMessages(params: {
    workspaceId: string;
    conversationId: string;
    threadId: string;
    limit?: number;
    before?: string;
  }): Promise<messageStructureTypes["MessageListResponse"]> {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    return await this.adapter.getMessages(params);
  }

  /**
   * Set authentication token
   */
  public setToken(token: string): void {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    this.adapter.setToken(token);
  }

  /**
   * Set callback handlers
   */
  public setCallbacks(callbacks: ProtocolHandlerCallbacks): void {
    if (!this.adapter) {
      throw new Error("Protocol handler not initialized");
    }
    this.adapter.setCallbacks(callbacks);
  }

  /**
   * Get current protocol type
   */
  public getProtocolType(): string {
    return this.protocolType;
  }

  /**
   * Cleanup resources
   */
  public async cleanup(): Promise<void> {
    if (this.adapter) {
      await this.adapter.cleanup();
    }
  }
}

// Export singleton instance
export const protocolHandler = ProtocolHandler.getInstance();
